import React, { FC, ReactElement, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert, Button, Card, Col, Divider, Form, Input, Row, Space, Typography,
} from "antd";
import {
  LockOutlined, MailOutlined, PhoneOutlined, UserAddOutlined, UserOutlined,
} from "@ant-design/icons";

import {
  selectErrors,
  selectIsAuthLoading,
  selectIsRegistered,
} from "../../redux-toolkit/auth/auth-selector";
import { registration } from "../../redux-toolkit/auth/auth-thunks";
import { resetAuthState, setAuthLoadingState } from "../../redux-toolkit/auth/auth-slice";
import { LoadingStatus, UserRegistration } from "../../types/types";

const Registration: FC = (): ReactElement => {
  const dispatch = useDispatch();
  const isRegistered = useSelector(selectIsRegistered);
  const isLoading = useSelector(selectIsAuthLoading);
  const errors = useSelector(selectErrors);

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(setAuthLoadingState(LoadingStatus.LOADED));
    return () => { dispatch(resetAuthState()); };
  }, []);

  const onClickSignUp = (userData: UserRegistration): void => {
    dispatch(registration(userData));
  };

  return (
    <Row justify="center" align="middle" style={{ minHeight: "90vh", backgroundColor: "#f5f7fa", padding: "40px 0" }}>
      <Col xs={22} sm={18} md={14} lg={8}>
        <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 4px 16px rgba(0,0,0,0.05)" }}>
          <Typography.Title level={3} style={{ textAlign: "center", marginBottom: 24 }}>
            <UserAddOutlined /> Sign Up
          </Typography.Title>

          {isRegistered && (
            <Alert
              message="Registration successful! Please Login."
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Form layout="vertical" name="registration-form" onFinish={onClickSignUp} requiredMark={false}>
            <Form.Item
              label="E-mail" name="email"
              validateStatus={errors.emailError ? "error" : ""}
              help={errors.emailError}
              rules={[{ required: true, message: "Please input your email!" }]}
            >
              <Input prefix={<MailOutlined />} placeholder="Enter your email" size="large" />
            </Form.Item>

            <Form.Item
              label="First Name" name="firstName"
              validateStatus={errors.firstNameError ? "error" : ""}
              help={errors.firstNameError}
              rules={[{ required: true, message: "Please input your first name!" }]}
            >
              <Input prefix={<UserOutlined />} placeholder="Enter your first name" size="large" />
            </Form.Item>

            <Form.Item
              label="Last Name" name="lastName"
              validateStatus={errors.lastNameError ? "error" : ""}
              help={errors.lastNameError}
              rules={[{ required: true, message: "Please input your last name!" }]}
            >
              <Input prefix={<UserOutlined />} placeholder="Enter your last name" size="large" />
            </Form.Item>

           <Form.Item
             label="Phone Number"
             name="phoneNumber"
             validateStatus={errors.phoneNumberError ? "error" : ""}
             help={errors.phoneNumberError}
             rules={[
               { required: true, message: "Please input your phone number!" },
               {
                 pattern: /^[0-9]{10}$/,
                 message: "Phone number must be exactly 10 digits",
               },
             ]}
           >
             <Input
               prefix={<PhoneOutlined />}
               placeholder="Enter 10-digit mobile number"
               maxLength={10}
               size="large"
             />
           </Form.Item>

            <Form.Item
              label="Password" name="password"
              validateStatus={errors.passwordError ? "error" : ""}
              help={errors.passwordError}
              rules={[{ required: true, message: "Please input your password!" }]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="Enter your password" size="large" />
            </Form.Item>

            <Form.Item
              label="Confirm Password" name="password2"
              validateStatus={errors.password2Error ? "error" : ""}
              help={errors.password2Error}
              rules={[{ required: true, message: "Please confirm your password!" }]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="Confirm your password" size="large" />
            </Form.Item>

            <Divider />

            <Form.Item>
              <Space style={{ width: "100%", justifyContent: "center" }}>
                <Button
                  type="primary" htmlType="submit" size="large"
                  icon={<UserAddOutlined />} loading={isLoading}
                  style={{ borderRadius: 8 }}
                >
                  Sign Up
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Col>
    </Row>
  );
};

export default Registration;